import { Controller } from "@hotwired/stimulus"

// Sprint 18 (04/06/2026) — A11y : injection automatique des attributs
// aria-label/role/tabindex sur les cellules éditables au mount. Évite
// de répéter 14× dans le partial _support_row.html.erb.
const FIELD_LABELS = {
  contact_address: "Adresse",
  contact_postal_code: "Code postal",
  contact_city: "Ville",
  contact_phone: "Téléphone",
  edof_dossier_number: "Numéro de dossier EDOF",
  platform: "Plateforme",
  start_date: "Date de début de formation",
  end_date: "Date de fin de formation",
  certification_date: "Date de certification",
  certification_status: "Statut de certification",
  support_product_id: "Support produit",
  supplier: "Fournisseur",
  support_price_ttc: "Prix support TTC",
  order_date: "Date de commande",
  delivery_date: "Date de livraison",
  billing_date: "Date de facturation"
}

export default class extends Controller {
  static targets = ["cell"]
  static values = { url: String }

  connect() {
    this.activeInput = null
  }

  cellTargetConnected(cell) {
    const field = cell.dataset.field
    const label = FIELD_LABELS[field] || field
    if (!cell.hasAttribute("aria-label")) cell.setAttribute("aria-label", `Modifier ${label}`)
    if (!cell.hasAttribute("role")) cell.setAttribute("role", "button")
    if (!cell.hasAttribute("tabindex")) cell.setAttribute("tabindex", "0")
    cell.addEventListener("keydown", (e) => {
      if ((e.key === "Enter" || e.key === " ") && !this.activeInput && e.target === cell) {
        e.preventDefault()
        this.edit({ currentTarget: cell })
      }
    })
  }

  edit(event) {
    if (this.activeInput) return
    const cell = event.currentTarget
    const field = cell.dataset.field
    const fieldType = cell.dataset.fieldType

    if (fieldType === "toggle") return

    this.originalHTML = cell.innerHTML
    const input = this.createInput(fieldType, field, cell)
    if (!input) return

    cell.innerHTML = ""
    cell.appendChild(input)
    this.activeInput = input
    input.focus()

    input.addEventListener("blur", () => this.save(cell, field, input))
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); input.blur() }
      if (e.key === "Escape") { this.cancel(cell); }
    })
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    const cell = event.currentTarget
    const field = cell.dataset.field

    let isOn = false
    if (cell.dataset.currentValue !== undefined && cell.dataset.currentValue !== "") {
      isOn = cell.dataset.currentValue === "true"
    } else {
      // fallback historique : regarde le texte du badge
      const currentBadge = cell.querySelector(".badge")
      isOn = currentBadge?.textContent?.trim() === "Validé"
    }

    this.patch(field, !isOn)
  }

  stop(event) {
    event.stopPropagation()
  }

  createInput(type, field, cell) {
    switch (type) {
      case "text": {
        const input = document.createElement("input")
        input.type = "text"
        input.className = "w-full text-xs px-2 py-1 border border-gold rounded bg-white focus:outline-none"
        input.value = this.extractValue(cell)
        return input
      }
      case "number": {
        const input = document.createElement("input")
        input.type = "number"
        input.step = "0.01"
        input.className = "w-24 text-xs px-2 py-1 border border-gold rounded bg-white focus:outline-none text-right"
        input.value = this.extractNumericValue(cell)
        return input
      }
      case "date": {
        const input = document.createElement("input")
        input.type = "date"
        input.className = "text-xs px-1 py-0.5 border border-gold rounded bg-white focus:outline-none"
        input.value = this.extractDateValue(cell)
        return input
      }
      case "select": {
        const select = document.createElement("select")
        select.className = "text-xs px-1 py-1 border border-gold rounded bg-white focus:outline-none"
        const emptyOpt = document.createElement("option")
        emptyOpt.value = ""
        emptyOpt.textContent = "—"
        select.appendChild(emptyOpt)

        const options = JSON.parse(cell.dataset.fieldOptions || "[]")
        const currentVal = this.extractSelectValue(cell)
        options.forEach(([label, value]) => {
          const opt = document.createElement("option")
          opt.value = value
          opt.textContent = label
          if (value === currentVal) opt.selected = true
          select.appendChild(opt)
        })
        select.addEventListener("change", () => select.blur())
        return select
      }
      default:
        return null
    }
  }

  extractValue(cell) {
    const span = cell.querySelector("span:not(.text-text-muted)")
    return span?.textContent?.trim() || ""
  }

  extractNumericValue(cell) {
    const text = cell.textContent.trim().replace(/[^\d.,]/g, "").replace(",", ".")
    return text || ""
  }

  extractDateValue(cell) {
    return cell.dataset.currentValue || ""
  }

  extractSelectValue(cell) {
    if (cell.dataset.currentValue !== undefined) return cell.dataset.currentValue
    const badge = cell.querySelector(".badge")
    if (!badge) return ""
    return badge.textContent.trim().toLowerCase()
  }

  save(cell, field, input) {
    const value = input.value
    this.patch(field, value, cell)
  }

  cancel(cell) {
    cell.innerHTML = this.originalHTML
    this.activeInput = null
  }

  patch(field, value, cell = null) {
    this.activeInput = null
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    // Sprint 11 (01/06/2026) — Si cell a data-broadcast="true" (ligne agrégée
    // MacBook 3 CPF), le serveur broadcast l'update aux deals frères du même mois.
    const broadcast = cell?.dataset?.broadcast === "true"
    const body = { deal_b2c: { [field]: value } }
    if (broadcast) body.broadcast = "true"

    // Feedback "saving" immédiat
    if (cell) this.flashCell(cell, "saving")

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    }).then(response => {
      if (response.ok) return response.json()
      throw new Error("Save failed")
    }).then(data => {
      // Sprint 15 (02/06/2026) — Warnings non bloquants (frères siblings fail)
      // ne doivent PAS empêcher l'affichage du success sur la cellule primary.
      if (data.warnings && data.warnings.length > 0) {
        console.warn("[support-edit] save OK avec warnings", data.warnings)
      }
      this.applyOptimisticUpdate(cell, field, value, data.deal)
      if (cell) this.flashCell(cell, "success")
    }).catch(err => {
      console.error("[support-edit] save failed", err)
      if (cell && this.originalHTML) this.cancel(cell)
      if (cell) this.flashCell(cell, "error")
    })
  }

  // Flash visuel de la cellule : jaune pendant save, vert si OK, rouge si erreur.
  // Utilise un outline coloré pour ne pas masquer le contenu.
  flashCell(cell, state) {
    const colors = {
      saving:  "rgba(245, 158, 11, 0.35)",  // ambre
      success: "rgba(16, 185, 129, 0.35)",  // emerald
      error:   "rgba(239, 68, 68, 0.40)"    // red
    }
    cell.style.transition = "background-color 0.2s ease-out"
    cell.style.backgroundColor = colors[state] || "transparent"
    if (state === "success" || state === "error") {
      setTimeout(() => { cell.style.backgroundColor = "transparent" }, 700)
    }
  }

  // Met à jour UNIQUEMENT la cellule modifiée (+ les cellules dépendantes
  // comme Statut/Prix HT/Statut certif qui sont déduits côté serveur).
  // Pas de re-render de toute la ligne → zéro scintillement.
  //
  // Sprint 1 (28/05/2026) — élargi pour patcher AUSSI marge, statut support
  // (delivery_status), et badge parrainage. Plus de F5 nécessaire après édition.
  applyOptimisticUpdate(cell, field, value, serverDeal) {
    if (cell) {
      this.renderCell(cell, field, value)
    }

    if (!serverDeal) return
    const row = this.element  // le <tr>

    // Toujours refresh ces cellules dérivées (peu importe le field source) :
    if (serverDeal.support_price_ht !== undefined) this.updateHtCell(row, serverDeal.support_price_ht)
    if (serverDeal.margin_percent !== undefined) this.updateMarginCell(row, serverDeal.margin_percent)
    if (serverDeal.delivery_status) this.updateDeliveryStatus(row, serverDeal.delivery_status)
    if (serverDeal.referral_status_label) this.updateReferralStatus(row, serverDeal.referral_status_label)
    if (serverDeal.certification_status) this.updateCertifStatus(row, serverDeal.certification_status)
    // Sprint 3 (01/06/2026) — Refresh cellule nom support si assignation produit
    if (field === "support_product_id") this.updateSupportProductCell(row, serverDeal)
  }

  renderCell(cell, field, value) {
    if (!cell) return
    if (cell.dataset.fieldType === "date" && value) {
      const d = new Date(value)
      cell.innerHTML = `<span class="text-sm text-text-primary">${d.toLocaleDateString("fr-FR")}</span>`
    } else if (cell.dataset.fieldType === "date" && !value) {
      cell.innerHTML = `<span class="text-sm text-text-muted/60 italic group-hover:text-gold/50 transition-colors">—</span>`
    } else if (cell.dataset.fieldType === "number" && value) {
      // Sprint 1 (28/05/2026) — format avec 2 décimales max (au lieu de 0)
      // pour ne pas perdre les centimes (0.01€ ne deviendra plus "—").
      const n = parseFloat(value)
      const formatted = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(n)
      cell.innerHTML = `<span class="text-sm text-text-secondary">${formatted} €</span>`
    } else if (cell.dataset.fieldType === "select") {
      // Sprint 14 (01/06/2026) — Pour les select, value = ID (ex support_product_id).
      // Faut afficher le LABEL correspondant, pas l'ID. Cherche dans data-field-options.
      cell.dataset.currentValue = value
      let displayText = value || "—"
      try {
        const options = JSON.parse(cell.dataset.fieldOptions || "[]")
        const match = options.find(([_label, id]) => String(id) === String(value))
        if (match) displayText = match[0]
      } catch (e) { /* ignore */ }
      cell.innerHTML = `<span class="text-sm text-text-secondary">${displayText}</span>`
    } else {
      cell.innerHTML = `<span class="text-sm text-text-secondary">${value || "—"}</span>`
    }
  }

  updateHtCell(row, htValue) {
    // Prix HT est la cellule juste après support_price_ttc (pas d'éditable)
    // On cherche par position relative. Fallback : ne rien faire.
    const ttcCell = row.querySelector('[data-field="support_price_ttc"]')
    if (!ttcCell) return
    const htCell = ttcCell.nextElementSibling
    if (!htCell) return
    if (htValue) {
      const formatted = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(htValue)
      htCell.innerHTML = `<span class="text-sm text-text-secondary">${formatted} €</span>`
    }
  }

  updateToggleCell(row, field, isOn, onLabel, onVariant, offLabel, offVariant, intermediate) {
    // Met à jour les badges Statut (support_validated) selon le nouveau state
    const cell = row.querySelector(`[data-field="${field}"]`)
    if (!cell) return
    cell.dataset.currentValue = String(isOn)
    if (isOn) {
      cell.innerHTML = this.badgeHtml(onLabel, onVariant)
    } else if (intermediate && intermediate[2]) {
      cell.innerHTML = this.badgeHtml(intermediate[0], intermediate[1])
    } else {
      cell.innerHTML = this.badgeHtml(offLabel, offVariant)
    }
  }

  updateCertifStatus(row, status) {
    const cell = row.querySelector('[data-field="certification_status"]')
    if (!cell) return
    const labels = { en_formation: "En formation", a_passer: "À passer", certifie: "Certifié", echec: "Échec" }
    const variants = { en_formation: "warning", a_passer: "info", certifie: "success", echec: "danger" }
    cell.dataset.currentValue = status
    cell.innerHTML = this.badgeHtml(labels[status] || status, variants[status] || "neutral")
  }

  // Sprint 1 (28/05/2026) — Marge % cellule lecture seule mais doit refresh
  // quand support_price_ttc change. Pas de data-field sur cellule marge, donc
  // on cherche par position : c'est la cellule juste après support_price_ht.
  updateMarginCell(row, marginPercent) {
    const ttcCell = row.querySelector('[data-field="support_price_ttc"]')
    if (!ttcCell) return
    const htCell = ttcCell.nextElementSibling
    const marginCell = htCell?.nextElementSibling
    if (!marginCell) return
    if (marginPercent === null || marginPercent === undefined) {
      marginCell.innerHTML = `<span class="text-xs text-text-muted">—</span>`
      return
    }
    let cls = "text-text-muted"
    if (marginPercent >= 30) cls = "text-success font-semibold"
    else if (marginPercent >= 15) cls = "text-gold font-semibold"
    else cls = "text-danger font-semibold"
    marginCell.innerHTML = `<span class="text-sm ${cls}">${Math.round(marginPercent)}%</span>`
  }

  // Sprint 1 (28/05/2026) — Statut support déduit (À commander / Commandé / Livré / Validé / —legacy)
  updateDeliveryStatus(row, status) {
    const cell = row.querySelector('[data-field="delivery_status"]')
    if (!cell) return
    const config = {
      validated: ["Validé", "success"],
      delivered: ["Livré", "info"],
      ordered: ["Commandé", "warning"],
      to_order: ["À commander", "danger"],
      legacy: null
    }
    const c = config[status]
    cell.innerHTML = c ? this.badgeHtml(c[0], c[1]) : `<span class="text-xs text-text-muted">—</span>`
  }

  // Sprint 3 (01/06/2026) — Cellule nom support : si support_product assigné, render
  // un lien cliquable vers canonical_url (Amazon). Sinon revient au "Sélectionner support".
  updateSupportProductCell(row, serverDeal) {
    const cell = row.querySelector('[data-field="support_product_id"]')
    if (!cell) return
    cell.dataset.currentValue = serverDeal.support_product_id || ""
    if (serverDeal.support_product_name && serverDeal.support_product_url) {
      cell.innerHTML = `<span class="inline-flex items-center gap-1">
        <a href="${serverDeal.support_product_url}" target="_blank" rel="noopener"
           class="text-sm font-medium text-gold hover:text-gold-dark hover:underline"
           data-action="click->support-edit#stop">${serverDeal.support_product_name}</a>
      </span>`
    } else {
      cell.innerHTML = `<span class="text-sm text-text-muted/60 italic group-hover:text-gold/50 transition-colors">Sélectionner support</span>`
    }
  }

  // Sprint 1 (28/05/2026) — Badge parrainage (Parrainé / Commandé / À distribuer / Cadeau ok)
  // re-rendered selon le label calculé serveur.
  updateReferralStatus(row, label) {
    const cell = row.querySelector('[data-field="referral_order_date"]')
    if (!cell) return
    const variants = {
      "Cadeau ok": "success",
      "Commandé": "info",
      "À distribuer": "warning",
      "Parrainé": "info"
    }
    cell.innerHTML = this.badgeHtml(label, variants[label] || "neutral")
  }

  badgeHtml(label, variant) {
    return `<span class="badge badge-${variant}">${label}</span>`
  }

  disconnect() {
    this.activeInput = null
  }
}
